import { useCallback, type FC } from "react";
import { useNavigate } from "react-router-dom";

import { useReduxEntitiesFetch } from "@ds/hooks/use-redux-entities-fetch";
import { useDeepRootSelector, useRootDispatch, useRootSelector } from "@ds/hooks";

import { EntityTypeName } from "@ds/model/entity-model";

import { deploymentsActions, DeploymentsRemoteOperation } from "@ds/modules/deployments/redux/slice";
import { createDeploymentModel, DeploymentScopeEnum } from "@ds/modules/deployments/utils/model";
import { MainTableDataTypeEnum } from "@ds/modules/table-data/utils/model";

import { ActionsBar } from "@ds/components/actions-bar";
import { Button, ButtonActionType, ButtonType } from "@ds/components/forms/buttons";
import { IconType } from "@ds/components/icons";
import { InfoViewToggleRibbonButton } from "@ds/components/info-view/info-view-toggle-ribbon-button";
import { MenuWithButton } from "@ds/components/menu-dropdown/menu-with-button";
import { confirmSuccess, confirmWarning } from "@ds/components/popups/confirmation-dialog";
import { pluralizeDeleteMessage } from "@ds/utils/localization/common";
import {
  pluralizeDeployDescription,
  pluralizeDeployMessage,
  pluralizeDeployName,
} from "@ds/utils/localization/deployment";
import { composeDetailsRouteByEntityType } from "@ds/utils/router";

import {
  selectAggregatedLocationForSelectedEntities,
  selectSelectedLocationIds,
  selectSelectedLocations,
} from "../../redux/selectors";
import { locationsActions } from "../../redux/slice";

export const LocationsTableActionsMenu: FC<{ onCreate: () => void; onDuplicate: () => void }> = ({
  onCreate,
  onDuplicate,
}) => {
  const navigate = useNavigate();

  const aggregatedLocation = useDeepRootSelector(selectAggregatedLocationForSelectedEntities);
  const selectedLocationsIds = useRootSelector(selectSelectedLocationIds);
  const selectedLocations = useRootSelector(selectSelectedLocations);

  const [toggleRefetch] = useReduxEntitiesFetch(
    { entityType: EntityTypeName.LOCATION },
    { tableType: MainTableDataTypeEnum.Locations },
  );

  const dispatch = useRootDispatch();
  const onUpdateClickHandler = useCallback(
    () => navigate(composeDetailsRouteByEntityType(aggregatedLocation)),
    [navigate, aggregatedLocation],
  );

  const onDeleteClickHandler = useCallback(
    () =>
      confirmWarning({
        message: pluralizeDeleteMessage("location", selectedLocationsIds.length),
        accept: () => dispatch(locationsActions.deleteLocations(selectedLocationsIds)),
      }),
    [dispatch, selectedLocationsIds],
  );

  const onDeployClickHandler = useCallback(
    () =>
      confirmSuccess({
        message: pluralizeDeployMessage("location", selectedLocationsIds.length),
        accept: () =>
          dispatch(
            deploymentsActions.createDeployment(
              {
                ...createDeploymentModel(),
                name: pluralizeDeployName(selectedLocations),
                description: pluralizeDeployDescription(selectedLocations),
                scope: DeploymentScopeEnum.Location,
                targets: selectedLocationsIds,
              },
              { remoteOperation: DeploymentsRemoteOperation.DEPLOYING },
            ),
          ),
      }),
    [dispatch, selectedLocations, selectedLocationsIds],
  );

  return (
    <ActionsBar>
      <MenuWithButton
        menuItems={[
          {
            label: "Deploy Content",
            icon: "pi pi-fw pi-sitemap",
            command: onDeployClickHandler,
          },
        ]}
        isDisabled={!selectedLocationsIds.length}
      />

      <Button ariaLabel="create button" buttonType={ButtonType.Round} icon={IconType.Create} onClick={onCreate} />
      {selectedLocationsIds.length === 1 && (
        <Button
          ariaLabel="duplicate button"
          buttonType={ButtonType.Round}
          icon={IconType.Duplicate}
          onClick={onDuplicate}
        />
      )}
      <Button
        ariaLabel="edit button"
        buttonType={ButtonType.Round}
        icon={IconType.Update}
        isDisabled={!selectedLocationsIds.length}
        onClick={onUpdateClickHandler}
      />

      <Button
        ariaLabel="delete button"
        buttonType={ButtonType.Round}
        actionType={ButtonActionType.Error}
        icon={IconType.Delete}
        isDisabled={!selectedLocationsIds.length}
        onClick={onDeleteClickHandler}
      />

      <Button
        ariaLabel="refresh button"
        buttonType={ButtonType.Round}
        icon={IconType.Refresh}
        onClick={toggleRefetch}
      />

      <InfoViewToggleRibbonButton />
    </ActionsBar>
  );
};
